/**
 * useTurnNotifier — in-app "it's your turn" watcher for OTHER games.
 *
 * The low-latency path is the private YOUR_TURN websocket event received by
 * the currently open game socket. This hook is the reconciliation fallback:
 * while the app is foregrounded and signed in, it polls the viewer's games
 * list and invokes `onNewTurn` if a turn event was missed — whether they're
 * sitting in the lobby or inside a different game. Only fresh turns are
 * eligible, and a poll that reveals several turns emits the newest one. The
 * game the viewer is currently looking at is skipped (they can already see
 * the turn UI).
 *
 * Mounted once at the root inside AuthProvider.
 */
import { useEffect, useRef } from 'react'
import { usePathname } from 'expo-router'
import type { GameResponse } from '@warcarp/api-types'
import { useAuth } from './useAuth'
import { useGames } from './useGames'
import { isMyTurn, myTurnIds } from '../lib/turnHelpers'
import { isRecentTurnBanner, isViewingGame } from '../lib/notificationBanner'

// The websocket is the low-latency path while a game is open. Polling remains
// a reconciliation path for the lobby, missed events, and reconnects.
const POLL_MS = 60_000

export function useTurnNotifier(onNewTurn: (game: GameResponse) => void): void {
  const { user } = useAuth()
  const pathname = usePathname()
  // Poll only while signed in (the query is shared with the home screen's
  // useGames via the same key, so this just adds the interval).
  const { data: games } = useGames({ refetchInterval: user ? POLL_MS : false })

  // Game ids that were already the viewer's turn as of the previous tick.
  const seen = useRef<Set<string> | null>(null)
  // Reset the baseline when the signed-in user changes.
  const lastUserId = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (!games || !user) return

    // First populated tick (or a new user): seed the baseline WITHOUT
    // notifying, so we don't banner for games that were already the viewer's
    // turn when the watcher started / they signed in.
    if (lastUserId.current !== user.id || seen.current === null) {
      lastUserId.current = user.id
      seen.current = myTurnIds(games, user.id)
      return
    }

    const previouslySeen = seen.current
    const newTurns: GameResponse[] = []
    for (const g of games) {
      // MUST use the same resolution as the myTurnIds baseline (playerIndex
      // FIELD match via isMyTurn, not players[currentPlayerIndex] array
      // indexing — see turnHelpers.ts). When the two disagreed (any game with
      // a playerIndex gap from a resigned/removed player), the game never
      // entered the baseline and the banner re-fired on every poll tick —
      // the "two in-app notifications for the same turn" bug.
      if (
        g.status === 'ACTIVE' &&
        isMyTurn(g, user.id) &&
        !previouslySeen.has(g.id) &&
        !isViewingGame(pathname, g.id) &&
        isRecentTurnBanner(g.turnStartedAt)
      ) {
        newTurns.push(g)
      }
    }
    // A cold resume can reveal several games at once. The root banner is a
    // single-slot notice, so emit only the newest eligible turn from a poll.
    const newest = newTurns
      .slice()
      .sort((a, b) => Date.parse(b.turnStartedAt ?? '') - Date.parse(a.turnStartedAt ?? ''))[0]
    if (newest) onNewTurn(newest)
    seen.current = myTurnIds(games, user.id)
  }, [games, user, pathname, onNewTurn])
}
